import { useState, useEffect } from 'react';
import { MonitorStatus, ApiResponse } from '../types';

interface MonitorSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsUpdate: () => void;
}

/**
 * 监控设置组件
 * 提供监控间隔时间设置功能
 */
const MonitorSettings: React.FC<MonitorSettingsProps> = ({
  isOpen,
  onClose,
  onSettingsUpdate
}) => {
  const [cronPattern, setCronPattern] = useState('0 */1 * * *');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<MonitorStatus | null>(null);
  const [useFixedInterval, setUseFixedInterval] = useState(true);
  const [dailyEndTasksEnabled, setDailyEndTasksEnabled] = useState(true);
  const [isDailyTasksLoading, setIsDailyTasksLoading] = useState(false);

  // 预设的监控间隔选项（与监控控制面板保持一致）
  const presetOptions = [
    { 
      label: '每5分钟', 
      value: '*/5 * * * *', 
      description: useFixedInterval ? '每5分钟整点执行 (01:05, 01:10, 01:15...)' : '高频监控，适合重要产品' 
    },
    { 
      label: '每10分钟', 
      value: '*/10 * * * *', 
      description: useFixedInterval ? '每10分钟整点执行 (01:10, 01:20, 01:30...)' : '较高频监控' 
    },
    { 
      label: '每15分钟', 
      value: '*/15 * * * *', 
      description: useFixedInterval ? '每15分钟整点执行 (01:15, 01:30, 01:45...)' : '适中频率监控' 
    },
    { 
      label: '每30分钟', 
      value: '*/30 * * * *', 
      description: useFixedInterval ? '每30分钟整点执行 (01:00, 01:30, 02:00...)' : '中等频率监控' 
    },
    { 
      label: '每小时', 
      value: '0 */1 * * *', 
      description: useFixedInterval ? '每小时整点执行 (01:00, 02:00, 03:00...)（推荐）' : '标准监控频率（推荐）' 
    },
    { 
      label: '每2小时', 
      value: '0 */2 * * *', 
      description: useFixedInterval ? '每2小时整点执行 (00:00, 02:00, 04:00...)' : '低频监控' 
    },
    { 
      label: '每4小时', 
      value: '0 */4 * * *', 
      description: useFixedInterval ? '每4小时整点执行 (00:00, 04:00, 08:00...)' : '较低频监控' 
    },
    { 
      label: '每6小时', 
      value: '0 */6 * * *', 
      description: useFixedInterval ? '每6小时整点执行 (00:00, 06:00, 12:00, 18:00)' : '低频率监控' 
    },
  ];

  // 获取当前监控状态
  useEffect(() => {
    if (isOpen) {
      fetchCurrentStatus();
    }
  }, [isOpen]);

  /**
   * 获取当前监控状态
   */
  const fetchCurrentStatus = async () => {
    try {
      const response = await fetch('/api/monitor/status');
      const result: ApiResponse<MonitorStatus> = await response.json();
      
      if (result.success && result.data) {
        setCurrentStatus(result.data);
        if (result.data.cronPattern) {
          setCronPattern(result.data.cronPattern);
        }
        if (result.data.useFixedInterval !== undefined) {
          setUseFixedInterval(result.data.useFixedInterval);
        }
        if (result.data.dailyEndTasks?.enabled !== undefined) {
          setDailyEndTasksEnabled(result.data.dailyEndTasks.enabled);
        }
      }
    } catch (error) {
              console.error('获取监控状态失败:', (error as Error).message);
    }
  };

  /**
   * 更新监控间隔
   */
  const handleUpdateInterval = async () => {
    setIsLoading(true);
    try {
      if (!cronPattern.trim()) {
        throw new Error('请选择有效的监控间隔');
      }

      const response = await fetch('/api/monitor/interval', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cronPattern: cronPattern,
          useFixedInterval: useFixedInterval
        }),
      });

      const result: ApiResponse = await response.json();

      if (result.success) {
        // 显示成功提示
        const toast = document.createElement('div');
        toast.className = 'toast toast-top toast-end';
        toast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ ${result.message}</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);

        // 更新状态并通知父组件
        await fetchCurrentStatus();
        onSettingsUpdate();
        onClose();
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      // 显示错误提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ ${(error as Error).message}</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 更新每日固定任务开关
   */
  const handleDailyEndTasksToggle = async (enabled: boolean) => {
    setIsDailyTasksLoading(true);
    try {
      const response = await fetch('/api/monitor/daily-end-tasks', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      });

      const result: ApiResponse = await response.json();

      if (result.success) {
        setDailyEndTasksEnabled(enabled);
        await fetchCurrentStatus();
        
        // 显示成功提示
        const toast = document.createElement('div');
        toast.className = 'toast toast-top toast-end';
        toast.innerHTML = `
          <div class="alert alert-success">
            <span>✅ ${result.message}</span>
          </div>
        `;
        document.body.appendChild(toast);
        setTimeout(() => document.body.removeChild(toast), 3000);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      // 恢复原状态
      setDailyEndTasksEnabled(!enabled);
      
      // 显示错误提示
      const toast = document.createElement('div');
      toast.className = 'toast toast-top toast-end';
      toast.innerHTML = `
        <div class="alert alert-error">
          <span>❌ ${(error as Error).message}</span>
        </div>
      `;
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } finally {
      setIsDailyTasksLoading(false);
    }
  };

  /**
   * 重置设置
   */
  const handleReset = () => {
    setCronPattern('0 */1 * * *');
    setUseFixedInterval(true);
    setDailyEndTasksEnabled(true);
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-4xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">监控设置</h3>
          <button 
            className="btn btn-ghost btn-circle" 
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* 当前状态 */}
        {currentStatus && (
          <div className="alert alert-info mb-6">
            <div className="flex flex-col items-start w-full">
              <div className="font-semibold mb-2">
                当前状态: {currentStatus.isMonitoring ? '🟢 监控中' : '🔴 已停止'}
              </div>
              <div className="text-sm opacity-80 mb-1">
                监控间隔: {currentStatus.cronPattern} ({currentStatus.intervalMinutes}分钟)
              </div>
              <div className="text-sm opacity-80 mb-1">
                时段监控任务: {currentStatus.dailyEndTasks.enabled ? '🟢 已启用' : '🔴 已禁用'}
              </div>
              {currentStatus.dailyEndTasks.enabled && currentStatus.dailyEndTasks.nextTasks.length > 0 && (
                <div className="text-xs opacity-60 mt-1">
                  下次执行: {new Date(currentStatus.dailyEndTasks.nextTasks[0].time).toLocaleString('zh-CN')}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* 监控模式选择 */}
          <div className="card bg-base-200 p-4">
            <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              监控时间模式
            </h4>
            
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input 
                  type="checkbox" 
                  className="toggle toggle-primary" 
                  checked={useFixedInterval}
                  onChange={(e) => setUseFixedInterval(e.target.checked)}
                />
                <div>
                  <div className="font-medium">固定时间间隔模式</div>
                  <div className="text-sm text-base-content/60">
                    {useFixedInterval 
                      ? '✅ 启用 - 按照整点时间执行监控（如01:00, 02:00, 03:00...）' 
                      : '❌ 禁用 - 使用传统cron调度（相对时间间隔）'
                    }
                  </div>
                </div>
              </label>
            </div>
            
            {useFixedInterval && (
              <div className="alert alert-info mt-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs">
                  固定时间间隔确保监控在准确的时间点执行，例如设置60分钟间隔时会在00:00、01:00、02:00等整点执行。
                </span>
              </div>
            )}
          </div>

          {/* 预设选项 */}
          <div>
            <h4 className="text-lg font-semibold mb-4">预设监控间隔</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {presetOptions.map((option) => (
                <label
                  key={option.value}
                  className={`card card-compact cursor-pointer transition-all ${
                    cronPattern === option.value
                      ? 'bg-primary text-primary-content shadow-lg'
                      : 'bg-base-200 hover:bg-base-300'
                  }`}
                >
                  <div className="card-body">
                    <div className="flex items-center">
                      <input
                        type="radio"
                        name="cronPattern"
                        value={option.value}
                        checked={cronPattern === option.value}
                        onChange={(e) => {
                          setCronPattern(e.target.value);
                        }}
                        className="radio radio-xs mr-3"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{option.label}</div>
                        <div className="text-xs opacity-70">{option.description}</div>
                        <div className="text-xs font-mono opacity-50 mt-1">
                          {option.value}
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 时段监控任务设置 */}
          <div className="card bg-base-200 p-4">
            <h4 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              时段监控任务
            </h4>
            
            <div className="form-control">
              <label className="label cursor-pointer justify-start gap-3">
                <input 
                  type="checkbox" 
                  className="toggle toggle-secondary" 
                  checked={dailyEndTasksEnabled}
                  onChange={(e) => handleDailyEndTasksToggle(e.target.checked)}
                  disabled={isDailyTasksLoading}
                />
                <div>
                  <div className="font-medium">
                    启用时段监控任务
                    {isDailyTasksLoading && <span className="loading loading-spinner loading-xs ml-2"></span>}
                  </div>
                  <div className="text-sm text-base-content/60">
                    {dailyEndTasksEnabled 
                      ? '✅ 启用 - 每小时59分自动执行监控，确保分时数据统计准确性' 
                      : '❌ 禁用 - 仅按常规监控间隔执行'
                    }
                  </div>
                </div>
              </label>
            </div>
            
            {dailyEndTasksEnabled && (
              <div className="alert alert-warning mt-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-xs">
                  <strong>重要提示：</strong>此功能独立于常规监控任务运行，即使停止常规监控也会继续执行，确保分时数据统计的准确性。
                  时间基于中国时区(Asia/Shanghai)，每小时执行1次固定监控。
                </span>
              </div>
            )}
            
            {currentStatus?.dailyEndTasks.enabled && currentStatus.dailyEndTasks.nextTasks.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium mb-2">下次执行时间:</div>
                <div className="grid grid-cols-1 gap-2">
                  {currentStatus.dailyEndTasks.nextTasks.map((task, index) => (
                    <div key={index} className="bg-base-300 rounded-lg p-3">
                      <div className="text-xs font-mono text-primary">
                        {new Date(task.time).toLocaleString('zh-CN')}
                      </div>
                      <div className="text-xs opacity-70">
                        {task.description}
                      </div>
                      <div className="text-xs opacity-50 mt-1">
                        {Math.floor(task.timeUntilNext / (1000 * 60 * 60))}小时{Math.floor((task.timeUntilNext % (1000 * 60 * 60)) / (1000 * 60))}分钟后
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* 操作按钮 */}
        <div className="modal-action">
          <button 
            className="btn btn-ghost" 
            onClick={handleReset}
            disabled={isLoading}
          >
            重置
          </button>
          <button 
            className="btn btn-error" 
            onClick={onClose}
            disabled={isLoading}
          >
            取消
          </button>
          <button 
            className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
            onClick={handleUpdateInterval}
            disabled={isLoading}
          >
            {isLoading ? '更新中...' : '保存设置'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MonitorSettings; 